import {
  Database,
  Mails,
  MessageSquare,
  Users,
  PieChart,
  GraduationCap,
  Map,
  Frame,
  ClipboardList,
  Link2,
  Package,
  DollarSign,
  BarChart3,
  ChartLine,
  Video,
  Dices,
  QrCode,
  UserSearch,
  Trophy,
  Brain,
  Activity,
  Workflow,
  type LucideIcon,
} from "lucide-react"

// Types for navigation configuration
export interface NavItem {
  title: string
  url: string
  icon?: LucideIcon
  isActive?: boolean
  items?: NavSubItem[]
  groups?: string[]
}

export interface NavSubItem {
  title: string
  url: string
  external?: boolean
  groups?: string[]
}

export interface NavProject {
  name: string
  url: string
  icon: LucideIcon
}

export interface Team {
  name: string
  logo: string
  plan: string
}

// Breadcrumb route labels
export const routeLabels: Record<string, string> = {
  "/": "Inicio",
  "/cuenta": "Mi Cuenta",
  "/doppler-flows": "Doppler Beta",
  "/doppler-flows/:id": "_skip",
  "/multichannel": "Multichannel",
  "/multichannel/seguimiento": "Seguimiento",
  "/multichannel/analytics": "Analytics",
  "/multichannel/calendario": "Calendario",
  "/multichannel/campania/nueva": "Nueva Campana",
  "/multichannel/reporte": "_skip",
  "/multichannel/reporte-whatsapp": "_skip",
  "/whatsapp": "WhatsApp",
  "/whatsapp/envio-masivo": "Envio Masivo",
  "/whatsapp/plantillas": "_skip",
  "/whatsapp/plantillas/twilio": "Plantillas",
  "/whatsapp/historial": "Historial",
  "/registros": "Registros",
  "/registros/reportes": "Reportes",
  "/registros/configuracion": "Configuracion",
  "/registros/reporte": "_skip",
  "/usuarios": "Usuarios",
  "/usuarios/grupos": "Grupos",
  "/usuarios/laboratorios": "Laboratorios",
  "/usuarios/areas": "Áreas",
  "/bd-unificada": "BD Unificada",
  "/bd-unificada/fuentes": "Fuentes",
  "/bd-unificada/cola": "Cola de verificación",
  "/bd-unificada/configuracion": "Configuración",
  "/medifarma": "Prospectos",
  "/medifarma/conecta/solicitar-registro": "Solicitar Registro",
  "/medifarma/seguimiento": "Seguimiento",
  "/link-tracker": "Link Tracker",
  "/link-tracker/urls": "URLs",
  "/link-tracker/crear": "Crear URL",
  "/link-tracker/analytics": "Analytics",
  "/visualizaciones": "Visualizaciones",
  "/passwords": "Contraseñas",
  "/cemc": "CEMC",
  "/cemc/configuracion": "Configuración",
  "/cemc-educacion": "CEMC Reportes",
  "/cemc-educacion/configuracion": "Configuración",
  "/programmatic": "Programmatic",
  "/programmatic/configuracion": "Configuración",
  "/analytics": "Analytics",
  "/analytics/configuracion": "Configuración",
  "/webinars": "Webinars",
  "/webinars/configuracion": "Configuración",
  "/dash/grupofarma/webinars": "Videos GrupoFarma",
  "/reportes-fidelizacion": "Fidelización",
  "/reportes-fidelizacion/configuracion": "Configuración",
  "/reportes-fidelizacion/:id": "_skip",
  "/sueroterapia": "Sueroterapia",
  "/sueroterapia/reporte": "Reporte",
  "/sueroterapia/configuracion": "Configuración",
  "/difare-expertos": "Difare Expertos",
  "/difare-expertos/configuracion": "Configuración",
  "/odf-expertos": "ODF Expertos",
  "/odf-expertos/configuracion": "Configuración",
  "/bd-clientes": "BD Clientes",
  "/bd-clientes/configuracion": "Configuración",
  "/bd-clientes/laboratorios": "Laboratorios",
  "/bd-clientes/laboratorios/seguimiento": "Seguimiento",
  "/bd-clientes/laboratorios/crear": "Nuevo Laboratorio",
  "/bd-clientes/laboratorios/configuracion": "Configuración",
  "/muestras-medicas": "Muestras Médicas",
  "/encuestas": "Encuestas",
  "/encuestas/crear": "Nueva Encuesta",
  "/encuestas/reportes": "Reportes",
  "/seguimiento-reportes": "Seguimiento Reportes",
  "/estadisticas": "Estadísticas",
  "/dash-admin": "Dash Reports",
  "/pricing/admin": "Pricing",
  "/sorteos": "Sorteos",
  "/sorteos/crear": "Crear Sorteo",
  "/codigos-qr": "Código QR",
  "/libro-qr": "Libro QR",
  "/libro-qr/crear": "Crear Libro",
  "/consumo-ia": "Consumo IA",
  "/consumo-meta": "Consumo Meta",
  "/configuracion": "Configuracion",
}

// Teams configuration
export const teams: Team[] = [
  {
    name: "Admin",
    logo: "/logo_tonal.png",
    plan: "Administración",
  },
  {
    name: "Tonal",
    logo: "/logo_tonal.png",
    plan: "Enterprise Platform",
  },
  {
    name: "Medifarma",
    logo: "/logo_tonal.png",
    plan: "Medifarma",
  },
]

// Main navigation items for Tonal team (sin Usuarios)
const navMainTonalItems: NavItem[] = [
  {
    title: "Multichannel",
    url: "#",
    icon: Mails,
    items: [
      { title: "Seguimiento", url: "/multichannel/seguimiento" },
      { title: "Analytics", url: "/multichannel/analytics" },
      { title: "Calendario", url: "/multichannel/calendario" },
      { title: "Nueva Campana", url: "/multichannel/campania/nueva" },
      { title: "Configuracion", url: "/configuracion", groups: ['admin'] },
    ],
  },
  {
    title: "WhatsApp",
    url: "#",
    icon: MessageSquare,
    items: [
      { title: "Envio Masivo", url: "/whatsapp/envio-masivo" },
      { title: "Plantillas", url: "/whatsapp/plantillas/twilio" },
      { title: "Historial", url: "/whatsapp/historial" },
    ],
  },
  {
    title: "Registros",
    url: "#",
    icon: ClipboardList,
    items: [
      { title: "Reportes", url: "/registros/reportes" },
      { title: "Configuracion", url: "/registros/configuracion" },
    ],
  },
  {
    title: "Visualizaciones",
    url: "#",
    icon: ChartLine,
    groups: ['Visualizaciones', 'admin'],
    items: [
      { title: "Reportes", url: "/visualizaciones" },
    ],
  },
  {
    title: "CEMC",
    url: "#",
    icon: GraduationCap,
    items: [],
  },
  {
    title: "CEMC Reportes",
    url: "#",
    icon: GraduationCap,
    items: [],
  },
  {
    title: "Programmatic",
    url: "#",
    icon: PieChart,
    items: [],
  },
  {
    title: "Analytics",
    url: "#",
    icon: BarChart3,
    items: [],
  },
  {
    title: "Webinars",
    url: "#",
    icon: Video,
    items: [],
  },
  {
    title: "Fidelización",
    url: "#",
    icon: Trophy,
    groups: ['admin', 'fidelizacion_admin', 'fidelizacion', 'fidelizacion_ventas_registros', 'Medifarma'],
    items: [
      { title: "Reportes", url: "/reportes-fidelizacion" },
      {
        title: "Configuración",
        url: "/reportes-fidelizacion/configuracion",
        groups: ['admin', 'fidelizacion_admin'],
      },
    ],
  },
  {
    title: "Sueroterapia",
    url: "#",
    icon: Activity,
    groups: ['admin', 'Sueroterapia-admin', 'Sueroterapia'],
    items: [
      { title: "Reporte", url: "/sueroterapia/reporte" },
      {
        title: "Configuración",
        url: "/sueroterapia/configuracion",
        groups: ['admin', 'Sueroterapia-admin'],
      },
    ],
  },
  {
    title: "Difare Expertos",
    url: "#",
    icon: BarChart3,
    groups: ['admin', 'Difare-admin', 'Difare', 'Difare-analytics'],
    items: [
      { title: "Analytics", url: "/difare-expertos" },
      {
        title: "Configuración",
        url: "/difare-expertos/configuracion",
        groups: ['admin', 'Difare-admin'],
      },
    ],
  },
  {
    title: "ODF Expertos",
    url: "#",
    icon: BarChart3,
    groups: ['admin', 'data-odf-doctors'],
    items: [
      { title: "Analytics", url: "/odf-expertos" },
      {
        title: "Configuración",
        url: "/odf-expertos/configuracion",
        groups: ['admin'],
      },
    ],
  },
  {
    title: "Muestras Médicas",
    url: "#",
    icon: Package,
    groups: ['admin', 'mm_admin', 'mm_cliente'],
    items: [
      { title: "Campañas", url: "/muestras-medicas" },
    ],
  },
  {
    title: "Pricing",
    url: "#",
    icon: DollarSign,
    groups: ['admin', 'pricing'],
    items: [
      { title: "Cotizaciones", url: "/pricing/admin" },
      { title: "Calculadora", url: "/pricing" },
    ],
  },
  {
    title: "Encuestas",
    url: "#",
    icon: ClipboardList,
    groups: ['admin', 'encuestas_admin', 'encuestas_clientes'],
    items: [
      { title: "Reportes", url: "/encuestas/reportes" },
      { title: "Administración", url: "/encuestas", groups: ['admin', 'encuestas_admin'] },
    ],
  },
  {
    title: "Prospectos",
    url: "#",
    icon: UserSearch,
    groups: ['Medifarma'],
    items: [
      { title: "Solicitar Registro", url: "/medifarma/conecta/solicitar-registro" },
      { title: "Seguimiento", url: "/medifarma/seguimiento" },
    ],
  },
  {
    title: "Sorteos",
    url: "#",
    icon: Dices,
    groups: ['sorteos', 'sorteos_admin'],
    items: [
      { title: "Dashboard", url: "/sorteos" },
      { title: "Crear Sorteo", url: "/sorteos/crear" },
    ],
  },
  {
    title: "Link Tracker",
    url: "#",
    icon: Link2,
    groups: ['link_tracker'],
    items: [
      { title: "Dashboard", url: "/link-tracker" },
      { title: "URLs", url: "/link-tracker/urls" },
      { title: "Crear URL", url: "/link-tracker/crear" },
    ],
  },
  {
    title: "Consumo IA",
    url: "#",
    icon: Brain,
    items: [
      { title: "Historial", url: "/consumo-ia" },
    ],
  },
  {
    title: "Consumo Meta",
    url: "#",
    icon: MessageSquare,
    items: [
      { title: "Historial", url: "/consumo-meta" },
    ],
  },
]

// Navigation items for Admin team
const navMainAdminItems: NavItem[] = [
  {
    title: "BD Clientes",
    url: "#",
    icon: Users,
    items: [
      { title: "Dashboard", url: "/bd-clientes" },
      { title: "Configuración", url: "/bd-clientes/configuracion" },
      { title: "Laboratorios", url: "/bd-clientes/laboratorios" },
    ],
  },
  {
    title: "Link Tracker",
    url: "#",
    icon: Link2,
    items: [
      { title: "Dashboard", url: "/link-tracker" },
      { title: "URLs", url: "/link-tracker/urls" },
      { title: "Crear URL", url: "/link-tracker/crear" },
    ],
  },
  {
    title: "Sorteos",
    url: "#",
    icon: Dices,
    items: [
      { title: "Dashboard", url: "/sorteos" },
      { title: "Crear Sorteo", url: "/sorteos/crear" },
    ],
  },
  {
    title: "Libro QR",
    url: "#",
    icon: QrCode,
    items: [
      { title: "Libros", url: "/libro-qr" },
    ],
  },
  {
    title: "Multichannel",
    url: "#",
    icon: Mails,
    items: [
      { title: "Seguimiento Reportes", url: "/seguimiento-reportes" },
    ],
  },
  {
    title: "Estadísticas",
    url: "#",
    icon: ChartLine,
    items: [
      { title: "Enterprise", url: "/estadisticas" },
    ],
  },
  {
    title: "Dash Reports",
    url: "#",
    icon: BarChart3,
    items: [
      { title: "Reportes", url: "/dash-admin" },
    ],
  },
  {
    title: "Muestras Médicas",
    url: "#",
    icon: Package,
    items: [
      { title: "Campañas", url: "/muestras-medicas" },
    ],
  },
  {
    title: "Usuarios",
    url: "#",
    icon: Users,
    items: [
      { title: "Listado", url: "/usuarios" },
      { title: "Grupos", url: "/usuarios/grupos" },
      { title: "Laboratorios", url: "/usuarios/laboratorios" },
      { title: "Áreas", url: "/usuarios/areas" },
    ],
  },
  {
    title: "BD Unificada",
    url: "#",
    icon: Database,
    groups: ['admin', 'BdUnificada-admin', 'BdUnificada'],
    items: [
      { title: "Contactos", url: "/bd-unificada" },
      {
        title: "Fuentes",
        url: "/bd-unificada/fuentes",
        groups: ['admin', 'BdUnificada-admin'],
      },
      {
        title: "Cola de verificación",
        url: "/bd-unificada/cola",
        groups: ['admin', 'BdUnificada-admin'],
      },
      {
        title: "Configuración",
        url: "/bd-unificada/configuracion",
        groups: ['admin', 'BdUnificada-admin'],
      },
    ],
  },
  {
    title: "Consumo IA",
    url: "#",
    icon: Brain,
    items: [
      { title: "Historial", url: "/consumo-ia" },
    ],
  },
  {
    title: "Consumo Meta",
    url: "#",
    icon: MessageSquare,
    items: [
      { title: "Historial", url: "/consumo-meta" },
    ],
  },
  {
    title: "Doppler Beta",
    url: "#",
    icon: Workflow,
    groups: ['admin'],
    items: [
      { title: "Flujos", url: "/doppler-flows" },
    ],
  },
]


// Quick access projects
export const navProjects: NavProject[] = [
  {
    name: "Campanas Activas",
    url: "/multichannel/seguimiento",
    icon: Frame,
  },
  {
    name: "Reportes",
    url: "/reportes",
    icon: PieChart,
  },
  {
    name: "Calendario",
    url: "/multichannel/calendario",
    icon: Map,
  },
]

// Navigation configurations by team
export const navMainTonal = navMainTonalItems  // Navigation for Tonal team (sin Usuarios)
export const navProjectsTonal = navProjects  // Full projects for Tonal team

export const navMainAdmin = navMainAdminItems  // Navigation for Admin team (solo Usuarios)
export const navProjectsAdmin = navProjects  // Same projects for Admin team

export const navMainMedifarma: NavItem[] = []  // Navigation for Medifarma team
export const navProjectsMedifarma: NavProject[] = []  // No projects for Medifarma team

// Helper function to generate breadcrumbs from pathname
// Rutas de breadcrumb que redirigen a una subpágina en vez de la raíz del módulo
const breadcrumbRedirects: Record<string, string> = {
  "/multichannel": "/multichannel/seguimiento",
}

export function generateBreadcrumbs(
  pathname: string,
  overrides?: Map<string, string>
): { label: string; path: string }[] {
  const paths = pathname.split("/").filter(Boolean)
  const breadcrumbs: { label: string; path: string }[] = []

  let currentPath = ""
  for (const segment of paths) {
    currentPath += `/${segment}`
    // Check for override first, then routeLabels, then capitalize segment
    const override = overrides?.get(currentPath)
    const label = override || routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1)
    // Skip segments marked with "_skip"
    if (label === "_skip") {
      continue
    }
    const linkPath = breadcrumbRedirects[currentPath] || currentPath
    breadcrumbs.push({ label, path: linkPath })
  }

  return breadcrumbs
}
